import { LUNCH, OFFICE_CLOSED, OFFICE_OPEN } from '@/config/constants'
import { ORGANIZATION_PROFILE_POLL_INTERVAL_MS } from '@/config/settingsConstants'
import {
	SET_OFFICE_OPEN_INFO,
	SET_ALERT_SETTING_POPUP,
	SET_ALERT_PREFERENCES,
	SET_ORGANIZATION_PROFILE,
	SET_SHOW_ORGANIZATION_RESTRICTED_ACCESS_NOTE,
	SET_ORGANIZATION_PANEL_EXPAND_FROM_ATTENTION,
} from '@/store/mutation-types'
import { api } from '@/store/helpers'
import moment from 'moment-timezone'
import { INITIAL_OFFICE_TIMING_INFO } from './state'

let scheduledTimeout = null
let organizationProfilePollIntervalId = null
let organizationProfilePollingOrganizationId = null
let organizationProfilePollingPromise = null
let organizationProfileRequestById = {}

/**
 * Resolve the active organization id for profile polling.
 *
 * @param {Object} rootGetters
 * @return {?Number|String}
 */
const resolveActiveOrganizationId = rootGetters => {
	const isOrganizationOnlyAdmin = rootGetters['auth/isOrganizationOnlyAdmin']
	const organizationId = rootGetters['auth/organizationId']
	const partner = rootGetters['partners/active']

	if (isOrganizationOnlyAdmin) {
		return organizationId
	}

	return partner?.organization_id || organizationId || null
}

/**
 * Normalize organization API response body.
 *
 * @param {Object} response
 * @return {Object}
 */
const getOrganizationResponseData = response => {
	const body = response?.get?.() || {}
	let data = body

	// Support `{ data: {...} }` and a root-level organization object.
	if (
		body.data &&
		typeof body.data === 'object' &&
		!Array.isArray(body.data)
	) {
		data = body.data
	}

	// Support nested organization keys used by some endpoints.
	if (
		data.organization &&
		typeof data.organization === 'object' &&
		!Array.isArray(data.organization)
	) {
		data = data.organization
	} else if (
		data.organization_profile &&
		typeof data.organization_profile === 'object' &&
		!Array.isArray(data.organization_profile)
	) {
		data = data.organization_profile
	}

	return data
}

/**
 * Whether the response looks like an organization profile payload.
 *
 * @param {Object} data
 * @return {Boolean}
 */
const hasOrganizationProfileFields = (data = {}) =>
	Boolean(
		data.id ||
			data.business_name !== undefined ||
			data.business_type !== undefined ||
			data.address_one !== undefined ||
			data.name !== undefined ||
			data.status !== undefined
	)

export default {
	async getOrganization({ commit, state, rootGetters }, payload) {
		if (!payload?.id) {
			return
		}

		const organizationId = String(payload.id)

		if (organizationProfileRequestById[organizationId]) {
			return organizationProfileRequestById[organizationId]
		}

		const existingProfileId = state.organizationProfile?.id

		if (
			!payload.force &&
			existingProfileId != null &&
			String(existingProfileId) === organizationId
		) {
			return state.organizationProfile
		}

		const request = api()
			.organization()
			.get(payload.id)
			.then(response => {
				const data = getOrganizationResponseData(response)
				const activeOrganizationId = resolveActiveOrganizationId(
					rootGetters
				)

				if (
					activeOrganizationId == null ||
					String(activeOrganizationId) !== organizationId
				) {
					return data
				}

				commit(SET_ORGANIZATION_PROFILE, data)
				return data
			})
			.finally(() => {
				delete organizationProfileRequestById[organizationId]
			})

		organizationProfileRequestById[organizationId] = request
		return request
	},

	/**
	 * Fetch the active partner/user organization profile.
	 *
	 * @param {Object} context
	 * @param {Object} [payload={}]
	 * @param {Boolean} [payload.force=false]
	 * @return {Promise<?Object>}
	 */
	fetchActiveOrganization({ dispatch, rootGetters }, payload = {}) {
		const organizationId = resolveActiveOrganizationId(rootGetters)

		if (!organizationId) {
			return Promise.resolve()
		}

		return dispatch('getOrganization', {
			id: organizationId,
			force: Boolean(payload.force),
		})
	},

	async updateOrganization({ commit, state }, { id, payload } = {}) {
		if (!id) {
			return
		}
		const response = await api()
			.organization()
			.patch(id, payload)
		const data = getOrganizationResponseData(response)
		const currentProfile = state.organizationProfile || {}

		// Prefer API response fields; fall back to request payload when body is empty.
		const nextProfile = hasOrganizationProfileFields(data)
			? { ...currentProfile, ...data, id: data.id || id }
			: { ...currentProfile, ...payload, id }

		commit(SET_ORGANIZATION_PROFILE, nextProfile)
		return nextProfile
	},

	stopOrganizationProfilePolling() {
		if (!organizationProfilePollIntervalId) {
			organizationProfilePollingOrganizationId = null
			return
		}

		window.clearInterval(organizationProfilePollIntervalId)
		organizationProfilePollIntervalId = null
		organizationProfilePollingOrganizationId = null
	},

	async startOrganizationProfilePolling({ dispatch, rootGetters, state }) {
		const organizationId = resolveActiveOrganizationId(rootGetters)
		if (!organizationId) {
			dispatch('stopOrganizationProfilePolling')
			return
		}

		const organizationIdKey = String(organizationId)
		const isPollingSameOrganization =
			organizationProfilePollIntervalId != null &&
			String(organizationProfilePollingOrganizationId) ===
				organizationIdKey

		if (isPollingSameOrganization) {
			return
		}

		if (organizationProfilePollingPromise) {
			return organizationProfilePollingPromise
		}

		organizationProfilePollingPromise = (async () => {
			dispatch('stopOrganizationProfilePolling')

			const profileId = state.organizationProfile?.id
			const hasProfileForOrganization =
				profileId != null && String(profileId) === organizationIdKey

			if (!hasProfileForOrganization) {
				await dispatch('getOrganization', { id: organizationId })
			}

			organizationProfilePollingOrganizationId = organizationIdKey

			organizationProfilePollIntervalId = window.setInterval(() => {
				const currentOrganizationId = resolveActiveOrganizationId(
					rootGetters
				)

				if (!currentOrganizationId) {
					dispatch('stopOrganizationProfilePolling')
					return
				}

				dispatch('getOrganization', { id: currentOrganizationId })
			}, ORGANIZATION_PROFILE_POLL_INTERVAL_MS)
		})().finally(() => {
			organizationProfilePollingPromise = null
		})

		return organizationProfilePollingPromise
	},

	syncOrganizationProfilePolling({ dispatch }) {
		return dispatch('startOrganizationProfilePolling')
	},

	async getOfficestatus({ commit, rootGetters, dispatch }, payload) {
		const activePartner = rootGetters['partners/active']

		if (!activePartner) {
			return
		}

		const { timezone: partnerTimeZone, id: activePartnerId } = activePartner

		const getMomentDateTime = time => {
			if (!time) return null
			const today = moment()
				.tz(partnerTimeZone)
				.format('YYYY-MM-DD')
			return moment.tz(
				`${today} ${time}`,
				'YYYY-MM-DD HH:mm',
				partnerTimeZone
			)
		}

		const normalizeTimeRange = (start, end) => {
			if (start && end && !end.isAfter(start)) {
				end.add(1, 'day')
			}
		}

		try {
			// Always fetch latest schedule
			let officeTimingsData = []
			let officeOpenInfo = INITIAL_OFFICE_TIMING_INFO

			if (!payload || !payload.length) {
				const response = await api()
					.partners(activePartnerId)
					.schedules()
					.get()
				officeTimingsData = response.get('data', [])
			} else {
				officeTimingsData = payload
			}

			// Hide office status if no timings are set
			const hasSomeTimings = officeTimingsData?.some(
				d => d?.starts_at && d?.ends_at
			)
			if (hasSomeTimings) {
				const hasLunchTimings = officeTimingsData?.some(
					d => d?.lunch_starts_at && d?.lunch_ends_at
				)
				officeOpenInfo = {
					...officeOpenInfo,
					visible: true,
					officeHoursSet: true,
					lunchHoursSet: hasLunchTimings,
				}
			}

			const now = moment().tz(partnerTimeZone)
			const currentDay = now.format('dddd').toLowerCase()
			const timings = officeTimingsData?.find(
				d => d?.day_name?.toLowerCase() === currentDay
			)

			if (timings?.is_open) {
				const start = getMomentDateTime(timings.starts_at)
				const end = getMomentDateTime(timings.ends_at)
				const lunchStart = getMomentDateTime(timings.lunch_starts_at)
				const lunchEnd = getMomentDateTime(timings.lunch_ends_at)
				// Handle overnight shifts
				normalizeTimeRange(start, end)
				normalizeTimeRange(lunchStart, lunchEnd)

				const nextMidnight = moment()
					.tz(partnerTimeZone)
					.add(1, 'day')
					.startOf('day')

				// Immediately update office status for current time
				if (now.isBetween(start, end, null, '[]')) {
					if (now.isBetween(lunchStart, lunchEnd, null, '[]')) {
						officeOpenInfo = {
							...officeOpenInfo,
							active: false,
							indicator: LUNCH,
						}
					} else {
						officeOpenInfo = {
							...officeOpenInfo,
							active: true,
							indicator: OFFICE_OPEN,
						}
					}
				}

				// Clear old timers before scheduling new ones
				if (scheduledTimeout) {
					clearTimeout(scheduledTimeout)
					scheduledTimeout = null
				}

				const delayArr = [
					start,
					lunchStart,
					lunchEnd,
					end,
					nextMidnight,
				]?.map(time => {
					if (!time) return -1
					return time.diff(now)
				})

				const delay = delayArr
					?.filter(i => i > 0)
					.sort((a, b) => a - b)[0]

				if (delay > 0) {
					scheduledTimeout = setTimeout(() => {
						dispatch('getOfficestatus')
					}, delay)
				}
			}

			commit(SET_OFFICE_OPEN_INFO, officeOpenInfo)
		} catch (err) {
			console.error('Failed to fetch office status', err)
			commit(SET_OFFICE_OPEN_INFO, {
				active: false,
				indicator: OFFICE_CLOSED,
				visible: true,
			})
		}
	},

	getAlertPopStatus({ commit, rootState }) {
		let isVisible = rootState.settings.alertSettingsPopup.visible
		let show = { visible: true }
		if (isVisible) {
			show = { visible: false }
		} else {
			show = { visible: true }
		}
		commit(SET_ALERT_SETTING_POPUP, show)
	},

	openAlertSettingsPanel({ commit }) {
		commit(SET_ALERT_SETTING_POPUP, { visible: true })
	},

	async setPreference({ rootState, commit }, payload) {
		const userId = rootState.auth?.user?.id
		const response = await api()
			.users()
			.setAlertPreferences(userId, payload)
		commit(SET_ALERT_PREFERENCES, payload)
		return response
	},

	async updatePreference({ rootState, commit }, payload) {
		const userId = rootState.auth?.user?.id
		const {
			id,
			payload: { omd },
		} = payload
		const response = await api()
			.users()
			.updateAlertPreferences(userId, id, { omd: omd || {} })
		commit(SET_ALERT_PREFERENCES, { id, omd })
		return response
	},

	async getAlertSettings({ commit, rootState }) {
		const userId = rootState.auth?.user?.id
		if (!userId) return
		const response = await api()
			.users()
			.getAlertSettings(userId)
		const { omd, id } = response?.response?.data
		commit(SET_ALERT_PREFERENCES, { id, omd })
	},

	openOrganizationRestrictedAccessFromAttention({ commit, rootGetters }) {
		commit(
			SET_SHOW_ORGANIZATION_RESTRICTED_ACCESS_NOTE,
			rootGetters['settings/requiresOrganizationProfileAttention']
		)
		commit(SET_ORGANIZATION_PANEL_EXPAND_FROM_ATTENTION, true)
	},

	clearOrganizationRestrictedAccessNote({ commit }) {
		commit(SET_SHOW_ORGANIZATION_RESTRICTED_ACCESS_NOTE, false)
	},

	clearOrganizationPanelExpandFromAttention({ commit }) {
		commit(SET_ORGANIZATION_PANEL_EXPAND_FROM_ATTENTION, false)
	},
}
